import es from 'date-fns/locale/es';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { default as React, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as PROFILE from "../../../constants/profile";
import { useAuth } from '../../../hooks/useAuth';
import moment from 'moment';
import { Dropdown } from 'primereact/dropdown';
import EditCashierTypeDialog from './EditCashierTypeDialog/EditCashierTypeDialog';

registerLocale('es', es)

function PaymentOrderReport() {

  // #region vars and consts

  const { hasPermission } = useAuth();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [report, setReport] = useState([]);  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isVisibleEditCashierTypeDialog, setIsVisibleEditCashierTypeDialog] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'externalOrderId': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'driverName': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'vehicleNo': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'departureTime': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'price': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'cashierModifiedDate': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'cashierDescription': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] }
  });

  // #endregion

  // #region events

  useEffect(() => {
    const todayMoment = moment();
    const startDateMoment = moment(todayMoment).startOf('month').startOf('day');
    const endDateMoment = todayMoment.startOf('day');
    setStartDate(startDateMoment.toDate());
    setEndDate(endDateMoment.toDate());
    getDrivers();
    scrollToTop();
  }, []);

  const onDriverChange = (e) => {
    console.log('driver', e.value);
    setSelectedDriver(e.value);
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilter(value);
  }

  const onSearchButtonPress = () => {
    getReport(
      startDate.valueOf(),
      endDate.valueOf(),
      selectedDriver.driverId
    );
  }

  const onExportExcelButtonPress = () => {
    exportAsExcel();
  };


  const onEditClick = (order) => {
    setSelectedOrder(order);
    setIsVisibleEditCashierTypeDialog(true);
  };

  const onHideEditCashierTypeDialog = () => {
    setIsVisibleEditCashierTypeDialog(false);
  };

  const onCashierTypeSaved = () => {
    setIsVisibleEditCashierTypeDialog(false);
    getReport(
      startDate.valueOf(),
      endDate.valueOf(),
      selectedDriver.driverId
    );
  };

  // #endregion

  // #region methods

  const getDrivers = () => {
    fetch(`${process.env.REACT_APP_API_URL}/driver/cmb`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          const _drivers = data.responseObject;
          setDrivers(_drivers);
        }
      });
  };

  const getReport = (
    startDate,
    endDate,
    driverId
  ) => {    
    setLoading(true);
    setReport([]);
    fetch(`${process.env.REACT_APP_API_URL}/order/findOrderCashier`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start: startDate,
        end: endDate,
        driverId
      })
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.info("Report:", data);      
        setReport(data.responseObject);        
      }
      setLoading(false);      
    }, (error) => {
      console.error("Error getting report", error);      
    });
  };

  const scrollToTop = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const exportAsExcel = () => {
    import('xlsx').then(xlsx => {
      const heading = [[
        "Número de servicio",
        "Conductor",
        "Vehículo",
        "Fecha de asignación",
        "Fecha de asignación",
        "Precio",
        "Fecha de Caja (y hora de último cambio de Estado)",
        "Fecha de Caja (y hora de último cambio de Estado)",
        "Estado de Caja"      
      ]];
      const worksheet = xlsx.utils.json_to_sheet(report, { origin: 'A2', skipHeader: true });
      xlsx.utils.sheet_add_aoa(worksheet, heading, { origin: 'A1' });
      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const fileName = 'ReporteDeCaja';
      import('file-saver').then(module => {
        if (module && module.default) {
          let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          let EXCEL_EXTENSION = '.xlsx';
          const data = new Blob([excelBuffer], {
            type: EXCEL_TYPE
          });
          module.default.saveAs(data, fileName + '_' + new Date().getTime() + EXCEL_EXTENSION);
        }
      });
    });
  };

  // #endregion

  // #region templates

  const filtersTemplate = (
    <>
      <Row>
        <Col md={3}>
          <DatePicker
            controlId="startDateInput"
            dateFormat="dd/MMM/yyyy"
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            locale="es"
            className="w-100 app_datepicker p-2"  />
        </Col>
        <Col md={3}>
          <DatePicker
            controlId="endDateInput"
            dateFormat="dd/MMM/yyyy"
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            locale="es"
            className="w-100 app_datepicker p-2"            
          />
        </Col>
        <Col md={3}>
          <Dropdown
            value={selectedDriver}
            options={drivers}
            onChange={onDriverChange}
            optionLabel="name"
            placeholder="Selecciona el chofer"
            className='p-inputtext-sm w-100'
          />
        </Col>
        <Col md={3}>
          <Button
            type="button"
            icon="pi pi-search"
            onClick={onSearchButtonPress}
            className="p-button-raised p-button-sm p-button-primary w-100"
            disabled={!selectedDriver || !startDate || !endDate}
            label='Buscar'
          />
        </Col>
      </Row>
    </>
  );

  const reportHeaderTemplate = (
    <>
      <Row>
        <Col>
          <span className="p-input-icon-left">
            <i className="pi pi-filter" />
            <InputText
              value={globalFilter}
              onChange={onGlobalFilterChange}
              placeholder="Filtrar"
              className='p-inputtext-sm'
            />
          </span>
        </Col>
        <Col>
          {
            hasPermission(PROFILE.PERMISSIONS.DOWNLOAD_REPORTS) && (
              <Button
                className="p-button-raised p-button-sm p-button-success mr-2 float-end"
                type="button"
                icon="pi pi-file-excel"
                data-pr-tooltip="XLS"
                label='Exportar'
                onClick={onExportExcelButtonPress}
              />
            )
          }
        </Col>
      </Row>
    </>
  );

  const priceColumnTemplate = (rowData) => {
    return rowData.price.toFixed(2);
  };

  const departureTimeTTemplate = (row) => {
    const departureTimeT = row.departureTimeT;
    if (departureTimeT > 0) {
      const departureTimeTMoment = moment(departureTimeT);
      return (
        <span>{departureTimeTMoment.format("DD/MM/YYYY hh:mm:ss a")}</span>
      );
    } else {
      return <></>;
    }
  }
  const paymentDateTTemplate = (row) => {
    const cashierModifiedDateT = row.cashierModifiedDateT;
    if (cashierModifiedDateT > 0) {
      const cashierModifiedDateTMoment = moment(cashierModifiedDateT);
      return (
        <span>{cashierModifiedDateTMoment.format("DD/MM/YYYY hh:mm:ss a")}</span>
      );
    } else {
      return <></>;
    }
  };

  const dataTableActionsTemplate = (row) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-primary p-button-text mr-2"
          onClick={() => onEditClick(row)} />
      </>
    );
  };

  const resultTemplate = (
    <>
      <div>
        <div className="card">
          <DataTable
            value={report}
            paginator
            className="p-datatable-customers"
            showGridlines
            stripedRows
            header={reportHeaderTemplate}
            rows={50}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            rowsPerPageOptions={[10, 25, 50]}
            filters={filters}
            filterDisplay="menu"
            loading={loading}
            responsiveLayout="scroll"
            globalFilterFields={[
              'externalOrderId',
              'driverName',
              'vehicleNo',
              'departureTime',
              'price',
              'cashierModifiedDate',
              'cashierDescription',
            ]}
            emptyMessage="No records found."
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          >               
            <Column
              className='app-w-50-px text-center'
              body={dataTableActionsTemplate}
              exportable={false}             
            />                        
            <Column field="externalOrderId" header="No. servicio" sortable filter filterPlaceholder="Buscar por No. servicio" className='app-w-150-px text-end' />
            <Column field="driverName" header="Conductor" sortable filter filterPlaceholder="Buscar por Conductor" className='app-w-250-px text-start' />
            <Column field="vehicleNo" header="Vehículo" sortable filter filterPlaceholder="Buscar por Vehículo" className='app-w-100-px text-start' />
            <Column field="departureTime" header="Fecha de asignación" sortable filter filterPlaceholder="Buscar por Fecha de asignación" className='app-w-200-px text-start' body={departureTimeTTemplate} />
            <Column field="Precio" header="Precio" sortable filter filterPlaceholder="Buscar por Precio" className='app-w-100-px text-end' body={priceColumnTemplate} />          
            <Column field="cashierModifiedDate" header="Fecha de Caja" sortable filter filterPlaceholder="Buscar por Fecha de Caja" className='app-w-200-px text-start' body={paymentDateTTemplate} />
            <Column field='cashierDescription' header="Estado de caja" sortable filter filterPlaceholder="Buscar por Estado de caja" className='app-w-200-px text-start' />
          </DataTable>
        </div>
      </div>
    </>
  );

  const editCashierTypeDialogTemplate = (
    <>
      {
        isVisibleEditCashierTypeDialog && (
          <EditCashierTypeDialog
            visible={isVisibleEditCashierTypeDialog}
            order={selectedOrder}
            onHide={onHideEditCashierTypeDialog}
            onCashierTypeSaved={onCashierTypeSaved}
          />
        )
      }
    </>
  );

  const reportTemplate = (
    <>
      <article>
        <Row>
          <Col>
            <h1 className='app-font'>
              Reporte de Caja
            </h1>
          </Col>
        </Row>
        <Row>
          <Col>
            {filtersTemplate}
          </Col>
        </Row>
        <Row className='pt-2'>
          <Col>
            {resultTemplate}
          </Col>
        </Row>
      </article>
      {editCashierTypeDialogTemplate}
    </>
  );

  // #endregion

  return (
    <>
      {reportTemplate}
    </>
  );
}

export default PaymentOrderReport;